import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { MdAdd, MdArrowForward, MdOutlineDelete } from 'react-icons/md';
import { usePromiseTracker } from 'react-promise-tracker';
import { Tooltip, Whisper } from 'rsuite';
import { LoadingMenu } from '../../../../../../components/LoadingSpinner';
import SelectPill from '../../../../../../components/SelectPill';
import { useElasticidadeCruzadaStore } from '../../ElasticidadeCruzada.store';
import { getSegments, type SegmentResponse } from '../../services';
import type { ChannelType } from '../../types';
import styles from './styles.module.scss';

const CHANNEL_TYPE_OPTIONS = [
  { label: 'Todos os canais', value: 'TODOS' },
  { label: 'Segmentos personalizados', value: 'SEGMENTOS' },
];

export const ChannelSelector = () => {
  const {
    produtoReferencia,
    produtosCompetidores,
    tipoConcorrente,
    segmentos,
    subsegmentos,
    showSubsegmentos,
    tipoCanal,
    setFilterValue,
    setFilterList,
    setFilterCache,
    setState,
  } = useElasticidadeCruzadaStore();
  const { promiseInProgress: isLoadingSegments } = usePromiseTracker({ area: 'elasticidade-segments' });

  const selectedProductBarcode = produtoReferencia.value?.[0] as string | undefined;
  const selectedCompetitorBarcode = produtosCompetidores.value?.[0] as string | undefined;

  const [segmentsData, setSegmentsData] = useState<SegmentResponse[]>([]);

  useEffect(() => {
    const canLoadSegments = tipoConcorrente === 'TODOS' || !!selectedCompetitorBarcode;

    if (tipoCanal === 'SEGMENTOS' && selectedProductBarcode && canLoadSegments) {
      getSegments({
        barcode: selectedProductBarcode,
        barcodeCompetitor: selectedCompetitorBarcode,
      }).then(data => {
        setSegmentsData(data);
      });
    } else {
      setSegmentsData([]);
    }
  }, [tipoCanal, tipoConcorrente, selectedProductBarcode, selectedCompetitorBarcode]);

  const segmentOptions = useMemo(() => segmentsData.map(s => s.segmento), [segmentsData]);

  const subsegmentosOptions = useMemo(() => {
    const selectedSegmentValue = segmentos.value?.[0];
    if (!selectedSegmentValue) return [];
    const selectedSegment = segmentsData.find(s => s.segmento.value === selectedSegmentValue);
    return selectedSegment?.subsegmentos || [];
  }, [segmentos.value, segmentsData]);

  const clearLocalizacao = useCallback(() => {
    setFilterValue('localizacao', []);
    setFilterList('localizacao', []);
    setFilterCache('localizacao', []);
  }, [setFilterCache, setFilterList, setFilterValue]);

  useEffect(() => {
    if (tipoCanal !== 'SEGMENTOS') return;
    if (!segmentsData.length) return;

    const selectedSegmentValue = segmentos.value?.[0] as string | undefined;
    const selectedSubsegmentValue = subsegmentos.value?.[0] as string | undefined;
    const hasSelectedSegment = !!selectedSegmentValue;
    const segmentStillAvailable = hasSelectedSegment ? segmentOptions.some(option => option.value === selectedSegmentValue) : true;

    if (hasSelectedSegment && !segmentStillAvailable) {
      setFilterValue('segmentos', []);
      setFilterList('segmentos', []);
      setFilterCache('segmentos', []);
      setFilterValue('subsegmentos', []);
      setFilterList('subsegmentos', []);
      setFilterCache('subsegmentos', []);
      clearLocalizacao();
      setState('showSubsegmentos', false);
      return;
    }

    if (!selectedSubsegmentValue) return;

    const subsegmentStillAvailable = subsegmentosOptions.some(option => option.value === selectedSubsegmentValue);

    if (!subsegmentStillAvailable) {
      setFilterValue('subsegmentos', []);
      setFilterList('subsegmentos', []);
      setFilterCache('subsegmentos', []);
      clearLocalizacao();
    }
  }, [
    clearLocalizacao,
    segmentOptions,
    segmentos.value,
    setFilterCache,
    setFilterList,
    setFilterValue,
    setState,
    subsegmentos.value,
    subsegmentosOptions,
    tipoCanal,
  ]);

  const handleChannelTypeChange = useCallback(
    (value: ChannelType) => {
      setState('tipoCanal', value || 'TODOS');
      clearLocalizacao();
      if (value === 'TODOS') {
        setFilterValue('segmentos', []);
        setFilterCache('segmentos', []);
        setFilterValue('subsegmentos', []);
        setFilterCache('subsegmentos', []);
        setState('showSubsegmentos', false);
      }
    },
    [clearLocalizacao, setFilterCache, setFilterValue, setState],
  );

  const handleSegmentChange = useCallback(
    (value: string) => {
      setFilterValue('segmentos', value ? [value] : []);
      setFilterValue('subsegmentos', []);
      setFilterCache('subsegmentos', []);
      clearLocalizacao();
      setState('showSubsegmentos', false);
    },
    [clearLocalizacao, setFilterCache, setFilterValue, setState],
  );

  const handleSegmentClean = useCallback(() => {
    setFilterValue('segmentos', []);
    setFilterCache('segmentos', []);
    setFilterValue('subsegmentos', []);
    setFilterCache('subsegmentos', []);
    clearLocalizacao();
    setState('showSubsegmentos', false);
  }, [clearLocalizacao, setFilterCache, setFilterValue, setState]);

  const handleToggleSubsegmentos = useCallback(() => {
    if (segmentos.value?.[0]) {
      setState('showSubsegmentos', !showSubsegmentos);
    }
  }, [segmentos.value, showSubsegmentos, setState]);

  const handleSubsegmentChange = useCallback(
    (value: string) => {
      setFilterValue('subsegmentos', value ? [value] : []);
      clearLocalizacao();
    },
    [clearLocalizacao, setFilterValue],
  );

  const handleSubsegmentClean = useCallback(() => {
    setFilterValue('subsegmentos', []);
    setFilterCache('subsegmentos', []);
    clearLocalizacao();
  }, [clearLocalizacao, setFilterCache, setFilterValue]);

  return (
    <>
      <div className={styles.filterSelectionWrapper}>
        <span className={styles.filterLabel}>CANAL</span>
        {/* @ts-ignore */}
        <SelectPill
          id="select-picker-pill"
          placeholder="Todos os canais"
          data={CHANNEL_TYPE_OPTIONS}
          value={tipoCanal}
          onSelect={handleChannelTypeChange}
          searchable={false}
          cleanable={false}
        />
        {tipoCanal === 'SEGMENTOS' && (
          <>
            <MdArrowForward className={styles.arrowIcon} />
            {/* @ts-ignore */}
            <SelectPill
              id="select-picker-pill"
              placeholder="Selecione o segmento"
              data={segmentOptions}
              value={segmentos.value?.[0] || null}
              onSelect={handleSegmentChange}
              onClean={handleSegmentClean}
              searchable={false}
              cleanable
              locale={{
                noResultsText: isLoadingSegments ? ' ' : 'Nenhum item encontrado',
                searchPlaceholder: 'Buscar item',
              }}
              renderMenu={(menu: React.ReactNode) => (
                <span>
                  <LoadingMenu area="elasticidade-segments" />
                  {menu}
                </span>
              )}
            />
            <Whisper
              placement="top"
              trigger="hover"
              delayShow={200}
              speaker={<Tooltip className={styles.subsegmentTooltip}>Inclua um filtro de subsegmentos à sua seleção.</Tooltip>}
            >
              <button
                type="button"
                className={styles.addButton}
                title="Adicionar subsegmento"
                onClick={handleToggleSubsegmentos}
                disabled={!segmentos.value?.[0]}
              >
                <MdAdd />
              </button>
            </Whisper>
            <span className={styles.helperText}>Segmentos criados previamente com as redes desejadas.</span>
          </>
        )}
      </div>

      {showSubsegmentos && subsegmentosOptions.length > 0 && (
        <div className={styles.filterSelectionWrapper}>
          <span className={styles.filterLabel} />
          <span className={styles.subsegmentLabel}>Subsegmentos personalizados</span>
          <MdArrowForward className={styles.arrowIcon} />

          {/* @ts-ignore */}
          <SelectPill
            id="select-picker-pill"
            placeholder="Selecione o subsegmento"
            data={subsegmentosOptions}
            value={subsegmentos.value?.[0] || null}
            onSelect={handleSubsegmentChange}
            onClean={handleSubsegmentClean}
            searchable={false}
            cleanable
          />

          <button type="button" className={styles.deleteButton} title="Remover subsegmento" onClick={handleToggleSubsegmentos}>
            <MdOutlineDelete />
          </button>
          <span className={styles.helperText}>Os subsegmentos exibidos pertencem aos segmentos selecionados acima.</span>
        </div>
      )}
    </>
  );
};
